/**
 * Hardware-acceleration detection for the SystemCheck flow. Looks at the
 * WebGL renderer string for known software-renderer patterns. When the
 * browser has hardware acceleration disabled (Chrome `chrome://settings →
 * System → Use hardware acceleration when available`, or Linux without a
 * GL driver) it falls back to one of these CPU rasterizers, which makes the
 * Viewer unusably slow.
 *
 * This is distinct from `gpuCapability`: HW-accel is a browser/OS toggle,
 * while the GPU denylist tracks hardware that is too old to drive the
 * Viewer reliably even with HW accel on.
 */

import { readWebGLRendererInfo } from './gpuCapability';
import type { WebGLRendererInfo } from './gpuCapability';

export type HardwareAccelerationStatus = 'enabled' | 'disabled' | 'unknown';

export type HardwareAccelerationResult = {
  status: HardwareAccelerationStatus;
  renderer: string;
  vendor: string;
  glVersion: string;
  /** When `disabled`, the human-readable software-renderer reason (e.g.
   *  "Software renderer: SwiftShader"). `null` otherwise. */
  softwareRendererMatch: Nullable<string>;
};

export type SoftwareRendererEntry = {
  pattern: RegExp;
  reason: string;
};

/**
 * Renderer-string patterns that indicate the browser is running without
 * hardware acceleration. Extend if Datadog data surfaces new variants.
 */
export const SOFTWARE_RENDERER_PATTERNS: ReadonlyArray<SoftwareRendererEntry> = [
  { pattern: /\bSwiftShader\b/i, reason: 'Software renderer: SwiftShader' },
  { pattern: /\bllvmpipe\b/i, reason: 'Software renderer: llvmpipe' },
  {
    pattern: /Microsoft Basic Render/i,
    reason: 'Software renderer: Microsoft Basic Render Driver',
  },
  {
    pattern: /\bMesa\b.*?Software Rasterizer/i,
    reason: 'Software renderer: Mesa Software Rasterizer',
  },
];

const matchSoftwareRenderer = (renderer: string): Nullable<string> => {
  for (const entry of SOFTWARE_RENDERER_PATTERNS) {
    if (entry.pattern.test(renderer)) return entry.reason;
  }
  return null;
};

/**
 * Reads the user's WebGL renderer and classifies it. Accepts an injected
 * reader for tests; defaults to the real WebGL canvas reader.
 */
export const detectHardwareAcceleration = (
  readRendererInfo: () => WebGLRendererInfo = readWebGLRendererInfo
): HardwareAccelerationResult => {
  const { renderer, vendor, glVersion } = readRendererInfo();

  if (!renderer) {
    return {
      status: 'unknown',
      renderer: '',
      vendor: '',
      glVersion: glVersion ?? '',
      softwareRendererMatch: null,
    };
  }

  const softwareRendererMatch = matchSoftwareRenderer(renderer);
  return {
    status: softwareRendererMatch != null ? 'disabled' : 'enabled',
    renderer,
    vendor,
    glVersion,
    softwareRendererMatch,
  };
};
